// Shows the Shopify Bundles that contain the current product.
// Liquid can't see bundle parents, so they're fetched from the tokenless Storefront API.
(() => {
  if (!customElements.get('shopify-bundles')) {
    customElements.define(
      'shopify-bundles',
      class ShopifyBundles extends HTMLElement {
        static API_VERSION = '2026-07';

        static QUERY = `
        query ShopifyBundles($handle: String!, $country: CountryCode, $language: LanguageCode)
        @inContext(country: $country, language: $language) {
          product(handle: $handle) {
            variants(first: 50) {
              nodes {
                groupedBy(first: 10) {
                  nodes {
                    id
                    availableForSale
                    price { amount }
                    product { title }
                    components(first: 10) {
                      nodes {
                        quantity
                        productVariant {
                          title
                          price { amount }
                          compareAtPrice { amount }
                          product {
                            title
                            handle
                            vendor
                            featuredImage { url altText width height }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

        connectedCallback() {
          this.section = this.closest('.shopify-bundles');
          this.bundleTemplate = this.querySelector('template[data-bundle]');
          this.cardTemplate = this.querySelector('template[data-card]');
          this.load();
        }

        async load() {
          let bundles = [];
          try {
            bundles = await this.fetchBundles();
          } catch (e) {
            console.error(e);
          }

          bundles.forEach((bundle, index) => this.appendChild(this.renderBundle(bundle, index)));
          this.section.classList.toggle('hidden', !bundles.length);
          if (bundles.length) this.showAnchors(bundles);
        }

        // Reveals the buy box link to this section, with the biggest saving across the bundles.
        showAnchors(bundles) {
          const saving = Math.max(...bundles.map((bundle) => this.getCompareAt(bundle) - toCents(bundle.price)));
          const savingLabel =
            saving > 0 ? (this.dataset.anchorSavingLabel || '').replace('[amount]', formatMoney(saving)) : '';

          document.querySelectorAll('[data-shopify-bundles-anchor]').forEach((anchor) => {
            anchor.querySelector('.shopify-bundles-anchor__text').textContent = this.dataset.anchorLabel;
            anchor.querySelector('.shopify-bundles-anchor__saving').textContent = savingLabel;
            anchor.classList.remove('hidden');
            anchor.addEventListener('click', (event) => {
              event.preventDefault();
              this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          });
        }

        getCompareAt(bundle) {
          return bundle.components.nodes.reduce(
            (sum, { productVariant, quantity }) => sum + toCents(productVariant.price) * quantity,
            0,
          );
        }

        async fetchBundles() {
          const response = await fetch(`/api/${ShopifyBundles.API_VERSION}/graphql.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              query: ShopifyBundles.QUERY,
              variables: {
                handle: this.dataset.productHandle,
                country: window.Shopify?.country,
                language: (window.Shopify?.locale || '').toUpperCase().replace('-', '_') || undefined,
              },
            }),
          });
          const { data } = await response.json();
          const variants = data?.product?.variants?.nodes || [];

          // A bundle can contain several variants of this product, so dedupe on the parent id.
          const bundles = new Map();
          variants.forEach((variant) =>
            variant.groupedBy.nodes.forEach((parent) => {
              // A sold-out bundle is a dead end, and a multipack of one product isn't a bundle in this layout.
              const products = new Set(parent.components.nodes.map(({ productVariant }) => productVariant.product.handle));
              if (parent.availableForSale && products.size >= 2) bundles.set(parent.id, parent);
            }),
          );
          return [...bundles.values()];
        }

        renderBundle(bundle, index) {
          const element = this.bundleTemplate.content.firstElementChild.cloneNode(true);
          const group = element.querySelector('shopify-bundle-group');
          const items = element.querySelector('.shopify-bundle__items');
          const components = bundle.components.nodes;

          const price = toCents(bundle.price);
          const compareAt = this.getCompareAt(bundle);

          // The section heading goes on the first bundle, the others are named after the bundle.
          const heading = index === 0 && this.dataset.heading ? this.dataset.heading : bundle.product.title;
          element.querySelector('.shopify-bundle__heading').textContent = heading;
          element.querySelector('[data-bundle-title]').textContent = bundle.product.title;

          group.dataset.variantId = bundle.id.split('/').pop();
          group.dataset.title = bundle.product.title;
          group.dataset.price = price;
          element.querySelector('[data-bundle-price]').textContent = formatMoney(price);
          if (compareAt > price) {
            element.querySelector('[data-bundle-compare]').textContent = formatMoney(compareAt);
            const saving = element.querySelector('[data-bundle-saving]');
            saving.textContent = saving.dataset.template.replace('[amount]', formatMoney(compareAt - price));
          }

          // Cards keep the width of a 3-product bundle; with 2 products the summary card fills the rest of the row.
          const columns = Math.max(components.length, 3) + 1;
          const summarySpan = columns - components.length;
          element.style.setProperty('--bundle-columns', columns);
          element.style.setProperty('--summary-span', summarySpan);
          element.classList.toggle('shopify-bundle--wide', summarySpan > 1);
          components.forEach((component) => items.appendChild(this.renderCard(component)));

          return element;
        }

        renderCard({ quantity, productVariant }) {
          const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
          const { product } = productVariant;
          const url = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;

          card.querySelectorAll('a').forEach((link) => (link.href = url));
          card.querySelector('[data-vendor]').textContent = product.vendor || '';
          card.querySelector('[data-title]').textContent = product.title;
          if (productVariant.title !== 'Default Title') {
            card.querySelector('[data-variant-title]').textContent = productVariant.title;
          }
          card.querySelector('[data-quantity]').textContent = `${quantity}x`;
          card.classList.toggle('shopify-bundle__card--current', product.handle === this.dataset.productHandle);

          // Show the line total, with the unit price underneath when there's more than one.
          const price = toCents(productVariant.price);
          const compareAt = toCents(productVariant.compareAtPrice);
          card.querySelector('[data-price]').textContent = formatMoney(price * quantity);
          if (compareAt > price) card.querySelector('[data-compare]').textContent = formatMoney(compareAt * quantity);
          if (quantity > 1) {
            const unitPrice = card.querySelector('[data-unit-price]');
            unitPrice.textContent = unitPrice.dataset.template.replace('[amount]', formatMoney(price));
          }

          const image = product.featuredImage;
          if (image) {
            const img = document.createElement('img');
            const src = new URL(image.url);
            img.srcset = [165, 355, 450, 710]
              .map((width) => {
                src.searchParams.set('width', width);
                return `${src.href} ${width}w`;
              })
              .join(', ');
            img.src = src.href;
            img.sizes = '(min-width: 768px) 260px, 62vw';
            img.alt = image.altText || product.title;
            img.loading = 'lazy';
            img.width = image.width;
            img.height = image.height;
            card.querySelector('.shopify-bundle__card-media .placeholder-svg').replaceWith(img);
          }

          return card;
        }
      },
    );
  }

  if (!customElements.get('shopify-bundle-group')) {
    customElements.define(
      'shopify-bundle-group',
      class ShopifyBundleGroup extends HTMLElement {
        connectedCallback() {
          this.cartDrawerElement = document.querySelector('cart-drawer');
          this.addButton = this.querySelector('.shopify-bundle__add');

          this.addButton.addEventListener('click', this.addToCart.bind(this));
        }

        // Adds the bundle parent variant; Shopify Bundles expands it into its components at checkout.
        addToCart(event) {
          event.preventDefault();
          const items = { items: [{ id: this.dataset.variantId, quantity: 1 }] };

          if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType != 'drawer') {
            FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { ...items } });
            return;
          }

          this.showErrorMessage();
          this.toggleLoading(true);

          const sections = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }),
          );

          const body = JSON.stringify({ ...items, sections, section_url: window.location.pathname });

          fetch(FoxTheme.routes.cart_add_url, { ...FoxTheme.utils.fetchConfig('javascript'), body })
            .then((response) => response.json())
            .then(async (parsedState) => {
              if (parsedState.status) {
                this.showErrorMessage(parsedState.description);
                return;
              }
              const cartJson = await (await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig())).json();
              cartJson.sections = parsedState.sections;

              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            this.trackAdd();
              document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
              this.cartDrawerElement && this.cartDrawerElement.show(this.addButton);
            })
            .catch((e) => console.error(e))
            .finally(() => this.toggleLoading(false));
        }

        // Lets the bundle's add-to-carts be measured apart from regular ones, e.g. in GTM or an A/B test.
        trackAdd() {
          const detail = {
            variantId: this.dataset.variantId,
            title: this.dataset.title,
            price: Number(this.dataset.price) / 100,
          };
          document.dispatchEvent(new CustomEvent('shopify-bundle:added', { detail }));
          window.dataLayer?.push({
            event: 'add_bundle_to_cart',
            bundle_id: detail.variantId,
            bundle_title: detail.title,
            bundle_price: detail.price,
          });
        }

        showErrorMessage(message = false) {
          const element = this.querySelector('.product-form__error-message');
          if (!element) return;
          element.hidden = !message;
          if (message) element.textContent = message;
        }

        toggleLoading(status) {
          this.addButton.classList.toggle('btn--loading', status);
          status ? this.addButton.setAttribute('disabled', true) : this.addButton.removeAttribute('disabled');
        }
      },
    );
  }

  function toCents(money) {
    return Math.round(parseFloat(money?.amount || 0) * 100);
  }

  function formatMoney(cents) {
    return window.FoxTheme?.Currency?.formatMoney
      ? FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat)
      : (cents / 100).toFixed(2).replace('.', ',');
  }
})();
